import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ReportStep } from '../../hooks/report/types';
import { renderReport, formatReportData } from '../../utils/reportRenderer';
import { formatProcessingTime } from '../../utils/reportProcessingUtils';
import '../../styles/reportContent.css';
import './SingleReportCard.css';

/**
 * Props for SingleReportCard component
 */
interface SingleReportCardProps {
  report: ReportStep;
  reportResult: any;
  isAdmin: boolean;
  getStatusText: (status: string) => string;
  isLoading?: boolean;
  onReloadRequest?: (reportIndex: number) => Promise<boolean | void>;
}

/**
 * SingleReportCard component - displays a single report with its results
 */
const SingleReportCard: React.FC<SingleReportCardProps> = ({
  report,
  reportResult,
  isAdmin,
  getStatusText,
  isLoading = false,
  onReloadRequest
}) => {
  const { t } = useTranslation('create');
  // State for tracking reload attempts
  const [reloadAttempts, setReloadAttempts] = useState<number>(0);
  const [isReloading, setIsReloading] = useState<boolean>(false);
  const maxReloadAttempts = 3;
  const containerRef = useRef<HTMLDivElement>(null);
  
  // Auto-reload effect for report results
  useEffect(() => {
    // If report is success but result is missing and not loading
    if (report.status === 'success' && !reportResult && !isLoading && !isReloading && reloadAttempts < maxReloadAttempts) {
      // Set reloading flag
      setIsReloading(true);
      
      // Exponential backoff delay
      const delay = Math.min(1000 * Math.pow(2, reloadAttempts), 10000); // Max 10 seconds
      
      // Delay before reloading
      const timer = setTimeout(async () => {
        if (onReloadRequest) {
          try {
            await onReloadRequest(report.index);
          } catch (error) {
            console.error(`Error auto-reloading results for report ${report.name}:`, error);
          } finally {
            // Increment attempts counter and reset reloading flag
            setReloadAttempts(prev => prev + 1);
            setIsReloading(false);
          }
        } else {
          setIsReloading(false);
        }
      }, delay);
      
      return () => clearTimeout(timer);
    }
  }, [report.status, report.index, report.name, reportResult, isLoading, isReloading, reloadAttempts, maxReloadAttempts, onReloadRequest]);

  // Effect for rendering report
  useEffect(() => {
    if (containerRef.current && reportResult && report.status === 'success') {
      // Clear container
      containerRef.current.innerHTML = '';
      
      try {
        // Format report data
        const formattedData = formatReportData(reportResult);
        
        // Render report
        renderReport(formattedData, containerRef.current);
        
        // Add unique class for container
        containerRef.current.classList.add(`report-container-${report.index}`);
        
        // Initialize charts
        const chartCanvases = containerRef.current.querySelectorAll('canvas[data-chart]');
        
        chartCanvases.forEach((canvas: Element, canvasIndex: number) => {
          try {
            const chartData = JSON.parse((canvas as HTMLElement).getAttribute('data-chart') || '{}');
            
            // Create unique ID for canvas с префиксом "gen"
            (canvas as HTMLElement).id = `gen-chart-${report.index}-${canvasIndex}`;
            
            // Render chart
            import('chart.js').then(({ Chart }) => {
              const canvasEl = canvas as HTMLCanvasElement;

              // Destroy existing Chart instance if already created by executeChartScripts
              const existing = Chart.getChart(canvasEl);
              if (existing) {
                existing.destroy();
              }

              const chartType = chartData.type || 'bar';
              const options = chartData.options || {
                responsive: true,
                maintainAspectRatio: false
              };

              // Position legend to the right for circular charts to prevent vertical overflow
              if (['pie', 'doughnut', 'polarArea'].includes(chartType)) {
                options.plugins = options.plugins || {};
                options.plugins.legend = {
                  ...(options.plugins.legend || {}),
                  position: 'right'
                };
              }

              new Chart(canvasEl, {
                type: chartType,
                data: {
                  labels: chartData.labels || [],
                  datasets: chartData.datasets || []
                },
                options
              });
            });
          } catch (error) {
            console.error(`Error initializing chart ${canvasIndex} for report ${report.index}:`, error);
          }
        });
      } catch (error) {
        console.error(`Error rendering report ${report.index}:`, error);
        containerRef.current.innerHTML = `<div class="error-message">
          <h4>Error rendering report</h4>
          <p>${error instanceof Error ? error.message : 'Unknown error'}</p>
        </div>`;
      }
    }
  }, [reportResult, report.status, report.index]);
  
  // Render component
  return (
    <div className="single-report-card">
      <div className="single-report-card-header">
        {/* Only show the report title if the report is not success or if there's no result yet */}
        {(report.status !== 'success' || !reportResult) && (
          <h4 style={{ margin: 0 }}>{report.name || t('results.report_fallback', { num: report.index + 1 })}</h4>
        )}
        <div className="report-status-info">
          <span className={`status-badge ${report.status}`}>
            {getStatusText(report.status)}
          </span>
          {report.processing_duration_seconds && (
            <span className="processing-time">
              {formatProcessingTime(report.processing_duration_seconds)}
            </span>
          )}
        </div>
      </div>
      
      <div className="single-report-card-body">
        {/* Loading state */}
        {report.status === 'success' && isLoading && (
          <div className="loading-spinner-container">
            <div className="loading-spinner">
              <div className="spinner"></div>
            </div>
            <p>Loading report results...</p>
          </div>
        )}
        
        {/* Success state with results */}
        {report.status === 'success' && !isLoading && reportResult && (
          <>
            {/* Formatted report view */}
            <div 
              className="report-container" 
              ref={containerRef}
            ></div>
            
            {/* JSON representation for admins */}
            {isAdmin && (
              <div className="json-result">
                <h4>Report JSON data (admin only)</h4>
                <pre>
                  {JSON.stringify(reportResult, null, 2)}
                </pre>
              </div>
            )}
          </>
        )}
        
        {/* Success state without results (loading) */}
        {report.status === 'success' && !isLoading && !reportResult && (
          <div className="loading-message">
            <p>Loading report results... {reloadAttempts > 0 ? `(attempt ${reloadAttempts}/${maxReloadAttempts})` : ''}</p>
            <div className="loading-spinner">
              <div className="spinner"></div>
            </div>
            {reloadAttempts >= maxReloadAttempts && !isReloading && (
              <div className="error-message">
                <p>Failed to load report results after several attempts. Try refreshing the page.</p>
                <button 
                  className="reload-button"
                  onClick={async () => {
                    if (onReloadRequest) {
                      setIsReloading(true);
                      try {
                        await onReloadRequest(report.index);
                        setReloadAttempts(0); // Reset attempts counter
                      } finally {
                        setIsReloading(false);
                      }
                    }
                  }}
                  disabled={isReloading}
                >
                  {isReloading ? 'Loading...' : 'Reload'}
                </button>
              </div>
            )}
          </div>
        )}
        
        {/* Error state */}
        {report.status === 'error' && report.error && (
          <div className="error-message">
            <h4>Error:</h4>
            <p>{report.error}</p>
          </div>
        )}
        
        {/* Pending state */}
        {report.status === 'pending' && (
          <p>Report is waiting to be processed.</p>
        )}
        
        {/* Processing state */}
        {report.status === 'processing' && (
          <div className="report-writing-animation">
            <div className="typing-container">
              <p className="typing-text">Analyzing data and creating report...</p>
            </div>
            
            <div className="chart-animation-container">
              {/* Bar chart animation */}
              <div className="chart-bar" style={{"--random-height": "60%"} as React.CSSProperties}></div>
              <div className="chart-bar" style={{"--random-height": "80%"} as React.CSSProperties}></div>
              <div className="chart-bar" style={{"--random-height": "40%"} as React.CSSProperties}></div>
              <div className="chart-bar" style={{"--random-height": "70%"} as React.CSSProperties}></div>
              <div className="chart-bar" style={{"--random-height": "50%"} as React.CSSProperties}></div>
              
              {/* Line chart animation */}
              <div className="chart-line"></div>
              <div className="chart-point"></div>
              <div className="chart-point"></div>
              <div className="chart-point"></div>
              <div className="chart-point"></div>
              <div className="chart-point"></div>
            </div>
            
            <p className="processing-message">Processing report, please wait...</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default SingleReportCard;
